import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useMapSpot } from '../../context/MapSpotContext'
import { useEntitySearch } from '../useEntitySearch'
import { useGeocoding } from '../useGeocoding'
import { useDistanceUnit } from '../useDistanceUnit'
import { useUserStats } from '../useUserStats'
import { useMapLocation } from '../useMapLocation'
import { useMapFiltersWithToggle } from '../useMapFiltersWithToggle'
import { useAuthForms } from '../useAuthForms'
import { useContactForm } from '../useContactForm'
import { useConfirmations } from '../useConfirmations'
import { useMapHandlers } from '../useMapHandlers'
import { useSpotMode } from '../useSpotMode'
import { useSharedMapState } from './useSharedMapState'
import { useSpotModeState } from './useSpotModeState'
import { useMapConfiguration } from './useMapConfiguration'
import { useEntityFetching } from '../useEntityFetching'
import { useSpotModeEffects } from '../useSpotModeEffects'
import { useEntitySelection } from '../useEntitySelection'
import { usePanelState } from '../usePanelState'
import { usePanelStateSync } from './usePanelStateSync'
import { useMapRefetchOnNavigation } from './useMapRefetchOnNavigation'
import { useUserLocationInit } from './useUserLocationInit'
import { useDeepLinkSearch } from '../useDeepLinkSearch'
import { useMapStateInit, useHashNavigation, useFeaturedEntity, useFeaturedEntityDisplay } from './useMapInitialization'
import { useShowUserLocationPersistence } from './useEntityDetailEffects'
import { useFeaturedEntityHandlers } from '../useFeaturedEntityHandlers'
import { useOpenNowFilter } from '../useOpenNowFilter'
import { handleShareURL, createPanelStateChangeHandler, handleCurrentLocationClick } from '../../utils/mapHandlerUtils'
import { useSpotDraftRestore } from './useSpotDraftRestore'

/**
 * Custom hook that encapsulates all state management for HomePageMap
 * This reduces the main component from 711 lines to under 300 lines
 */
export function useHomePageMapState({
  isFullscreenMode,
  isImmersiveMode,
  onTabChange,
  panelState,
  activeTab
}) {
  const { t, i18n } = useTranslation()
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const { convertFromKm } = useDistanceUnit()

  // Spot mode context
  const { isSpotModeActive, activateSpotMode, deactivateSpotMode, spotModeCallback, preSelectedEntity, initialLocation } = useMapSpot()

  // Shared map state
  const {
    entities,
    setEntities,
    userLocation,
    setUserLocation,
    radius,
    setRadius,
    searchLocation,
    setSearchLocation,
    searchLocationName,
    setSearchLocationName,
    showUserLocation,
    setShowUserLocation,
    isLoading,
    setIsLoading,
    mapRef,
    markerRefs,
    currentOpenPopupRef,
    hasInitialized,
    hasShownLocationError,
    lastFetchTime,
    previousLocationKey,
    savedMapViewRef,
    activeMapLocation
  } = useSharedMapState({ isEntityMode: false })

  // Geocoding
  const { geocode, reverseGeocode: reverseGeocodeFn, isGeocoding } = useGeocoding()

  // Location tracking
  const [locationSource, setLocationSource] = useState(null)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [isGeolocationAvailable] = useState('geolocation' in navigator)

  // Featured entity state
  const [featuredEntity, setFeaturedEntity] = useState(null)
  const [showingFeaturedEntity, setShowingFeaturedEntity] = useState(false)
  const [featuredEntityReason, setFeaturedEntityReason] = useState(null)

  // Search sort
  const [searchSortBy, setSearchSortBy] = useState('recent')

  // Entity selection
  const {
    entitySearchQuery,
    setEntitySearchQuery,
    selectedEntityId,
    setSelectedEntityId,
    focusedEntityId,
    setFocusedEntityId,
    isolatedEntityId,
    setIsolatedEntityId
  } = useEntitySelection()

  // Map location hooks
  const { getUserLocation, getLastSavedLocation, saveLocation } = useMapLocation({ 
    t, 
    fetchCallback: null,
    reverseGeocode: reverseGeocodeFn 
  })

  // Open Now filter
  const { openNowIds, openNowActive, toggleOpenNow } = useOpenNowFilter()

  // Filters
  const { filteredEntities, activeFilters, handleFilterToggle } = useMapFiltersWithToggle(entities, isolatedEntityId, openNowIds, openNowActive)

  // Initialization hooks
  useMapStateInit({ 
    setEntities, 
    setUserLocation, 
    setRadius, 
    setSearchLocation, 
    setSearchLocationName, 
    getLastSavedLocation, 
    enabled: true 
  })
  
  useHashNavigation({ 
    onTabChange, 
    isImmersiveMode, 
    enabled: true 
  })
  
  useFeaturedEntity({ 
    setFeaturedEntity, 
    enabled: true 
  })
  
  useFeaturedEntityDisplay({
    featuredEntity, 
    hasInitialized, 
    isGettingLocation, 
    userLocation, 
    entities, 
    filteredEntities,
    isLoading, 
    setShowingFeaturedEntity, 
    setFeaturedEntityReason, 
    showingFeaturedEntity, 
    searchLocationName
  })

  // Entity search
  const {
    results: entitySearchResults,
    isSearching,
    showDropdown: showSearchDropdown,
    setShowDropdown: setShowSearchDropdown
  } = useEntitySearch({
    query: entitySearchQuery,
    userLocation,
    radius,
    searchLocation,
    sortBy: searchSortBy,
  })

  // Panel state
  const {
    isSpotPanelMinimized,
    setIsSpotPanelMinimized,
    isSearchPanelMinimized,
    setIsSearchPanelMinimized
  } = usePanelState()

  // Spot mode state
  const {
    spotModeLocation,
    setSpotModeLocation,
    spotModeEntity,
    setSpotModeEntity,
    spotModeEntityType,
    setSpotModeEntityType,
    spotModeNotes,
    setSpotModeNotes,
    spotModeEntityStatus,
    setSpotModeEntityStatus,
    spotModeImages,
    setSpotModeImages,
    isSubmittingSpot,
    setIsSubmittingSpot,
    entitySearchInputRef,
    entitySearchInputRefAlt,
    showShareOptions,
    setShowShareOptions,
    spotModeStartTime,
    setSpotModeStartTime,
    spotModeLocationSetTime,
    setSpotModeLocationSetTime,
    spotModeSocials,
    setSpotModeSocials
  } = useSpotModeState()

  // Pending share card (after successful sighting submit)
  const [pendingShareSighting, setPendingShareSighting] = useState(null)

  // Pending auth submit (draft preserved while user logs in)
  const [pendingAuthSubmit, setPendingAuthSubmit] = useState(false)

  // Share handler
  const handleShare = useCallback(async (platform = null) => {
    await handleShareURL({ 
      url: window.location.origin, 
      title: 'TrckIt', 
      text: t('map.shareText') || 'Discover food trucks and street vendors near you!', 
      platform, 
      t 
    })
  }, [t])

  // Panel state sync
  usePanelStateSync({
    isImmersiveMode,
    panelState,
    activeTab,
    setIsSearchPanelMinimized
  })

  // Auth forms
  const {
    loginFormData,
    loginErrors,
    handleLoginChange,
    handleLoginSubmit,
    registerFormData,
    registerErrors,
    handleRegisterChange,
    handleRegisterSubmit,
    isAuthLoading,
    googleAuth
  } = useAuthForms(t, null, isImmersiveMode, onTabChange)

  // Panel state change handler
  const handlePanelStateChange = useCallback(
    createPanelStateChangeHandler(panelState, activeTab, isImmersiveMode, onTabChange, 'HomePageMap'),
    [panelState, activeTab, isImmersiveMode, onTabChange]
  )

  // Contact form
  const {
    contactFormData,
    contactErrors,
    isContactSubmitting,
    contactSuccess,
    handleContactChange,
    handleContactSubmit,
    setContactSuccess
  } = useContactForm(t)

  // Data fetching
  const { fetchNearbyEntities } = useEntityFetching(
    t,
    radius,
    setIsLoading,
    setEntities
  )

  // Confirmations
  const {
    pendingConfirmations,
    loadingConfirmations,
    confirmingId,
    loadPendingConfirmations,
    handleConfirmSighting
  } = useConfirmations(user, t, () => {
    if (userLocation) {
      fetchNearbyEntities(userLocation, null, true)
    }
  }, activeMapLocation)

  // User statistics
  const { stats, loading: statsLoading, refetch: refetchStats } = useUserStats(user?.id, !!user, activeMapLocation)

  // Wrapper so useSpotMode / useSpotModeEffects can trigger GPS with no arguments and
  // still have the result update userLocation state (required for reactive re-centering).
  const triggerSpotGeolocation = useCallback(() => {
    getUserLocation({
      onSuccess: (location) => setUserLocation(location)
    })
  }, [getUserLocation, setUserLocation])

  // Spot mode effects
  useSpotModeEffects({
    isSpotModeActive,
    userLocation,
    preSelectedEntity,
    spotModeEntity,
    initialLocation,
    spotModeLocation,
    isFullscreenMode,
    isLoading,
    mapRef,
    savedMapViewRef,
    setSpotModeEntity,
    setEntitySearchQuery,
    setUserLocation,
    setSpotModeLocation,
    setSpotModeNotes,
    setSpotModeEntityStatus,
    setIsSpotPanelMinimized,
    getUserLocation: triggerSpotGeolocation
  })

  // Map configuration
  const { defaultCenter, mapCenter, mapZoom, mapBounds } = useMapConfiguration({
    isEntityMode: false,
    entityLocation: null,
    userLocation: userLocation ? { lat: userLocation.latitude, lng: userLocation.longitude } : null,
    searchLocation: searchLocation ? { lat: searchLocation.latitude, lng: searchLocation.longitude } : null,
    entities: entities,
    activeMapLocation: null
  })

  // User location initialization
  useUserLocationInit({
    userLocation,
    setUserLocation,
    setSearchLocation,
    setSearchLocationName,
    setLocationSource,
    setIsGettingLocation,
    getUserLocation,
    fetchCallback: fetchNearbyEntities,
    hasInitialized,
    isGeolocationAvailable,
    isGettingLocation
  })

  // Map refetch on navigation
  useMapRefetchOnNavigation({
    userLocation,
    fetchCallback: fetchNearbyEntities,
    location: { pathname: window.location.pathname },
    hasInitialized,
    previousLocationKey,
    forceFresh: true
  })

  // Persist showUserLocation
  useShowUserLocationPersistence(showUserLocation)

  // Map interaction handlers
  const {
    handleRadiusChange,
    handleBoundsChange,
    geocodeAddress: geocodeAddressBase,
    handleSelectEntity
  } = useMapHandlers({
    userLocation,
    setUserLocation,
    setSearchLocationName,
    fetchNearbyEntities,
    geocode,
    searchLocation,
    entities,
    setEntities,
    setSelectedEntityId,
    setEntitySearchQuery,
    setShowSearchDropdown,
    mapRef,
    isImmersiveMode,
    setRadius,
    onTabChange,
    panelState,
    setIsSearchPanelMinimized,
    saveLocation
  })

  // Geocode address wrapper
  const geocodeAddress = useCallback(async () => {
    await geocodeAddressBase()
    setLocationSource('geocoded')
  }, [geocodeAddressBase])

  // Featured entity handlers
  const {
    handleFeaturedViewDetails, 
    handleFeaturedShowOnMap, 
    handleFeaturedJoinChannel
  } = useFeaturedEntityHandlers({
    handleSelectEntity, 
    setUserLocation, 
    setSearchLocation, 
    setSearchLocationName, 
    fetchNearbyEntities,
    setLocationSource, 
    mapRef, 
    setSelectedEntityId, 
    setFocusedEntityId, 
    onTabChange, 
    onChannelSelected: () => {}, 
    user, 
    t
  })

  // Deep link search
  useDeepLinkSearch({
    activeTab,
    searchLocation,
    setSearchLocation,
    setRadius,
    geocodeAddress
  })

  // Spot mode handlers
  const {
    handleSpotModeActivation,
    handleSpotSubmit
  } = useSpotMode({

    user,
    activateSpotMode,
    deactivateSpotMode,
    spotModeEntity,
    spotModeLocation,
    spotModeNotes,
    spotModeEntityStatus,
    spotModeImages,
    spotModeSocials,
    spotModeEntityType,
    setIsSubmittingSpot,
    spotModeStartTime,
    setSpotModeStartTime,
    spotModeLocationSetTime,
    setSpotModeLocationSetTime,
    userLocation,
    radius,
    fetchNearbyEntities,
    setEntities,
    spotModeCallback,
    isImmersiveMode,
    onTabChange,
    setPendingShareSighting,
    setPendingAuthSubmit,
    setIsSpotPanelMinimized,
    getUserLocation: triggerSpotGeolocation,
    t
  })

  // Restore spot mode draft after login (covers both regular login and OAuth redirect)
  useSpotDraftRestore({
    user,
    pendingAuthSubmit,
    setPendingAuthSubmit,
    setSpotModeEntity,
    setSpotModeLocation,
    setSpotModeNotes,
    setSpotModeImages,
    setSpotModeEntityType,
    activateSpotMode,
  })

  // Current location handler
  const handleCurrentLocation = useCallback((onPanelMinimize) => {
    handleCurrentLocationClick({
      getUserLocation, 
      setUserLocation, 
      setSearchLocation, 
      setSearchLocationName,
      setShowUserLocation,
      setIsSearchPanelMinimized,
      onPanelMinimize,
      fetchCallback: fetchNearbyEntities, 
      setLocationSource, 
      setIsGettingLocation,
      saveLocation,
      mapRef, 
      isEntityMode: false, 
      isGeolocationAvailable, 
      isGettingLocation
    })
  }, [getUserLocation, setUserLocation, setSearchLocation, setSearchLocationName, setShowUserLocation, setIsSearchPanelMinimized, fetchNearbyEntities, setLocationSource, setIsGettingLocation, saveLocation, isGeolocationAvailable, isGettingLocation])

  // Return all state and handlers
  return {
    // Core
    t,
    i18n,
    user,
    isAuthenticated,
    logout,
    navigate,
    convertFromKm,
    
    // Map state
    entities,
    setEntities,
    userLocation,
    setUserLocation,
    radius,
    setRadius,
    searchLocation,
    setSearchLocation,
    searchLocationName,
    setSearchLocationName,
    showUserLocation,
    setShowUserLocation,
    isLoading,
    mapRef,
    markerRefs,
    currentOpenPopupRef,
    
    // Location tracking
    locationSource,
    isGeocoding,
    isGettingLocation,
    isGeolocationAvailable,
    
    // Featured entity
    featuredEntity,
    showingFeaturedEntity,
    setShowingFeaturedEntity,
    featuredEntityReason,
    
    // Entity selection
    entitySearchQuery,
    setEntitySearchQuery,
    selectedEntityId,
    focusedEntityId,
    setFocusedEntityId,
    isolatedEntityId,
    setIsolatedEntityId,
    
    // Filters
    filteredEntities,
    activeFilters,
    handleFilterToggle,
    openNowActive,
    toggleOpenNow,
    
    // Search
    entitySearchResults,
    isSearching,
    showSearchDropdown,
    setShowSearchDropdown,
    searchSortBy,
    setSearchSortBy,
    
    // Panel state
    isSpotPanelMinimized,
    setIsSpotPanelMinimized,
    isSearchPanelMinimized,
    setIsSearchPanelMinimized,
    
    // Spot mode
    isSpotModeActive,
    spotModeLocation,
    setSpotModeLocation,
    spotModeEntity,
    setSpotModeEntity,
    spotModeEntityType,
    setSpotModeEntityType,
    spotModeNotes,
    setSpotModeNotes,
    spotModeEntityStatus,
    setSpotModeEntityStatus,
    spotModeSocials,
    setSpotModeSocials,
    spotModeImages,
    setSpotModeImages,
    isSubmittingSpot,
    spotModeStartTime,
    deactivateSpotMode,
    entitySearchInputRef,
    entitySearchInputRefAlt,
    pendingShareSighting,
    setPendingShareSighting,
    pendingAuthSubmit,
    
    // Forms
    loginFormData,
    loginErrors,
    handleLoginChange,
    handleLoginSubmit,
    registerFormData,
    registerErrors,
    handleRegisterChange,
    handleRegisterSubmit,
    isAuthLoading,
    googleAuth,
    contactFormData,
    contactErrors,
    isContactSubmitting,
    contactSuccess,
    handleContactChange,
    handleContactSubmit,
    setContactSuccess,
    
    // Confirmations
    pendingConfirmations,
    loadingConfirmations,
    confirmingId,
    loadPendingConfirmations,
    handleConfirmSighting,
    
    // Stats
    stats,
    statsLoading,
    
    // Map configuration
    defaultCenter,
    mapCenter,
    mapZoom,
    mapBounds,
    
    // Handlers
    handleRadiusChange,
    handleBoundsChange,
    geocodeAddress,
    handleSelectEntity,
    handleShare,
    handlePanelStateChange,
    handleFeaturedViewDetails,
    handleFeaturedShowOnMap,
    handleFeaturedJoinChannel,
    handleSpotModeActivation,
    handleSpotSubmit,
    handleCurrentLocation,
    activeMapLocation,
    
    // Map drag functionality
    fetchNearbyEntities,
    reverseGeocode: reverseGeocodeFn,
    saveLocation
  }
}
